// ClipEmbed.tsx — a clip post's card in feeds (1.27.60): the source video's frame with the
// clip's length, and where it comes from. Opens the clip's own watch view. A clip whose
// source this reader can't see (or that is gone) shows a plain note instead — never more
// than the source itself would.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';
import type { PostClip } from '@/core/models/post';
import { formatClock } from '@/features/videos/player/chapters';

const Card = styled(Link)`
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: 12px;
  margin-top: 8px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: inherit;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Thumb = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  span {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.72);
    color: #fff;
    font: 600 11px ${({ theme }) => theme.font.body};
    font-variant-numeric: tabular-nums;
  }
`;

const Text = styled.div`
  display: grid;
  align-content: center;
  gap: 4px;
  min-width: 0;
  strong {
    font: 700 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.display};
    color: ${({ theme }) => theme.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  small {
    font: 500 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const Gone = styled.p`
  margin: 8px 0 0;
  padding: 10px 12px;
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function ClipEmbed({ clip, postId }: { clip: PostClip; postId: string }) {
  if (clip.unavailable || !clip.video) {
    return <Gone data-testid="clip-unavailable">This clip’s video isn’t available.</Gone>;
  }
  const seconds = Math.round((clip.endMs - clip.startMs) / 1000);
  return (
    <Card
      to={`/videos/${postId}`}
      data-testid="clip-embed"
      aria-label={`Clip from ${clip.sourceTitle}, ${seconds} seconds`}
    >
      <Thumb>
        {clip.video.posterUrl && <img src={clip.video.posterUrl} alt="" loading="lazy" />}
        <span>
          <Icon name="scissors" size={12} />
          {seconds} s
        </span>
      </Thumb>
      <Text>
        <strong>{clip.sourceTitle}</strong>
        <small>
          <MetaRow>
            <span>Clip</span>
            <span>
              {formatClock(clip.startMs / 1000)}–{formatClock(clip.endMs / 1000)}
            </span>
            <span>from {clip.sourceAuthorHandle}</span>
          </MetaRow>
        </small>
      </Text>
    </Card>
  );
}
